<template>
  <v-container fluid class="pa-0">
    <!-- Site Material Balances -->
    <v-card class="mb-4">
      <v-card-title>
        <v-icon left>mdi-package-variant</v-icon>
        {{ $t('inventory.site_materials') }}
        <v-spacer />
        <v-btn v-if="$vuetify.breakpoint.smAndUp" color="primary" @click="openDispatchNoteDialog">
          <v-icon left>mdi-truck-delivery</v-icon>
          {{ $t('inventory.new_dispatch_note') }}
        </v-btn>
      </v-card-title>
      <v-card-text class="pa-0">
        <v-simple-table dense>
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left">{{ $t('inventory.item_name') }}</th>
                <th class="text-right">{{ $t('inventory.quantity') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="balance in siteBalances" :key="balance.item.id">
                <td>{{ balance.item.name }}</td>
                <td class="text-right">
                  <span :class="balance.quantity <= 0 ? 'red--text' : ''">
                    {{ formatQuantity(balance.quantity) }} {{ balance.item.unit || 'kpl' }}
                  </span>
                </td>
              </tr>
              <tr v-if="siteBalances.length === 0 && !loadingBalances">
                <td colspan="2" class="text-center grey--text">{{ $t('common.no_data') }}</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
        <v-progress-linear v-if="loadingBalances" indeterminate color="primary" />
      </v-card-text>
    </v-card>

    <!-- Dispatch Notes -->
    <v-card class="mb-4">
      <v-card-title>
        <v-icon left>mdi-file-document-multiple</v-icon>
        {{ $t('inventory.dispatch_notes') }}
      </v-card-title>
      <v-data-table
        :headers="dispatchHeaders"
        :items="dispatchNotes"
        :loading="loadingDispatchNotes"
        :items-per-page="10"
        class="elevation-1"
      >
        <template v-slot:item.direction="{ item }">
          <v-chip :color="item.direction === 'TO_SITE' ? 'success' : 'warning'" small dark>
            <v-icon left small>{{ item.direction === 'TO_SITE' ? 'mdi-arrow-right' : 'mdi-arrow-left' }}</v-icon>
            {{ $t('inventory.directions.' + item.direction) }}
          </v-chip>
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip :color="item.status === 'COMPLETED' ? 'success' : 'grey'" small dark>
            {{ $t('inventory.statuses.' + item.status) }}
          </v-chip>
        </template>
        <template v-slot:item.createdAt="{ item }">
          {{ formatDate(item.createdAt) }}
        </template>
        <template v-slot:item.createdBy="{ item }">
          {{ item.createdByUser ? item.createdByUser.name : '-' }}
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn icon @click="viewDispatchNote(item)" :title="$t('common.view')">
            <v-icon>mdi-eye</v-icon>
          </v-btn>
          <v-btn
            v-if="item.status === 'DRAFT'"
            icon
            color="primary"
            @click="editDispatchNote(item)"
            :title="$t('common.edit')"
          >
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <v-btn
            v-if="item.status === 'DRAFT'"
            icon
            color="success"
            @click="completeDispatchNote(item)"
            :title="$t('inventory.complete_dispatch')"
          >
            <v-icon>mdi-check</v-icon>
          </v-btn>
          <v-btn
            v-if="item.status === 'DRAFT'"
            icon
            color="error"
            @click="deleteDispatchNote(item)"
            :title="$t('common.delete')"
          >
            <v-icon>mdi-delete</v-icon>
          </v-btn>
          <v-btn
            v-if="item.status === 'COMPLETED' && ($isTenantAdmin || $isAdmin)"
            icon
            small
            color="primary"
            @click="editCompletedDispatchNote(item)"
            :title="$t('inventory.edit_completed_dispatch')"
          >
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- Transaction History -->
    <v-expansion-panels v-model="transactionHistoryExpanded" class="mb-4">
      <v-expansion-panel>
        <v-expansion-panel-header>
          <div class="d-flex align-center">
            <v-icon left>mdi-history</v-icon>
            {{ $t('inventory.site_transaction_history') }}
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-data-table
            :headers="transactionHeaders"
            :items="transactions"
            :loading="loadingTransactions"
            :items-per-page="10"
            class="elevation-1"
            sort-by="createdAt"
            sort-desc
          >
            <template v-slot:item.transactionType="{ item }">
              <v-chip :color="getTransactionTypeColor(item.transactionType)" small dark>
                {{ $t('inventory.transaction_types.' + item.transactionType) }}
              </v-chip>
            </template>
            <template v-slot:item.quantity="{ item }">
              <span :class="item.quantity >= 0 ? 'green--text' : 'red--text'">
                {{ item.quantity >= 0 ? '+' : '' }}{{ formatQuantity(item.quantity) }}
              </span>
            </template>
            <template v-slot:item.createdAt="{ item }">
              {{ formatDate(item.createdAt) }}
            </template>
          </v-data-table>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- New/Edit Dispatch Note Dialog -->
    <dispatch-note-dialog ref="dispatchNoteDialog" :site="site" @saved="onDispatchSaved" />

    <!-- View Dispatch Note Dialog -->
    <v-dialog v-model="viewDispatchDialog" max-width="900" :fullscreen="$vuetify.breakpoint.xsOnly">
      <v-card v-if="selectedDispatchNote">
        <v-card-title>
          {{ $t('inventory.dispatch_note') }}: {{ selectedDispatchNote.dispatchNumber }}
          <v-spacer />
          <v-btn icon @click="viewDispatchDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-simple-table>
            <template v-slot:default>
              <tbody>
                <tr>
                  <td><strong>{{ $t('inventory.warehouse') }}</strong></td>
                  <td>{{ selectedDispatchNote.warehouseName || '-' }}</td>
                </tr>
                <tr>
                  <td><strong>{{ $t('inventory.direction') }}</strong></td>
                  <td>
                    <v-chip :color="selectedDispatchNote.direction === 'TO_SITE' ? 'success' : 'warning'" small dark>
                      {{ $t('inventory.directions.' + selectedDispatchNote.direction) }}
                    </v-chip>
                  </td>
                </tr>
                <tr>
                  <td><strong>{{ $t('inventory.status') }}</strong></td>
                  <td>
                    <v-chip :color="selectedDispatchNote.status === 'COMPLETED' ? 'success' : 'grey'" small dark>
                      {{ $t('inventory.statuses.' + selectedDispatchNote.status) }}
                    </v-chip>
                  </td>
                </tr>
                <tr>
                  <td><strong>{{ $t('inventory.created_at') }}</strong></td>
                  <td>{{ formatDate(selectedDispatchNote.createdAt) }}</td>
                </tr>

              </tbody>
            </template>
          </v-simple-table>

          <v-divider class="my-4" />

          <h4>{{ $t('inventory.items') }}</h4>
          <v-simple-table>
            <template v-slot:default>
              <thead>
                <tr>
                  <th>{{ $t('inventory.item_name') }}</th>
                  <th>{{ $t('inventory.pieces') }}</th>
                  <th>{{ $t('inventory.unit_quantity') }}</th>
                  <th>{{ $t('inventory.total_quantity') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in selectedDispatchNote.items" :key="item.id">
                  <td>
                    <span>{{ item.item.name }}</span>
                    <div v-if="item.item.typeName" class="caption grey--text">
                      {{ item.item.typeName }}
                    </div>
                  </td>
                  <td>{{ item.pieces }}</td>
                  <td>{{ item.unitQuantity || '-' }}</td>
                  <td>{{ formatQuantity(item.totalQuantity) }} {{ item.item.unit || 'kpl' }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Confirm Dialog -->
    <confirm-dialog ref="confirmDialog" />

  </v-container>
</template>

<script>
import inventoryApi from '@/api/inventory';
import ConfirmDialog from '@/components/ConfirmDialog';
import DispatchNoteDialog from '@/components/Inventory/DispatchNoteDialog';

export default {
  name: 'SiteMaterials',
  components: { ConfirmDialog, DispatchNoteDialog },
  props: {
    site: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loadingBalances: false,
      loadingDispatchNotes: false,
      loadingTransactions: false,

      siteBalances: [],
      dispatchNotes: [],
      transactions: [],

      // View dispatch dialog
      viewDispatchDialog: false,
      selectedDispatchNote: null,



      // Transaction history expansion (collapsed on mobile by default)
      transactionHistoryExpanded: null,

      // Table headers
      balanceHeaders: [
        { text: this.$t('inventory.item_name'), value: 'item.name' },
        { text: this.$t('inventory.quantity'), value: 'quantity' },
      ],
      dispatchHeaders: [
        { text: this.$t('inventory.dispatch_number'), value: 'dispatchNumber' },
        { text: this.$t('inventory.warehouse'), value: 'warehouseName' },
        { text: this.$t('inventory.direction'), value: 'direction' },
        { text: this.$t('inventory.status'), value: 'status' },
        { text: this.$t('inventory.created_at'), value: 'createdAt' },
        { text: this.$t('inventory.created_by'), value: 'createdBy' },
        { text: '', value: 'actions', sortable: false },
      ],
      transactionHeaders: [
        { text: this.$t('inventory.item_name'), value: 'item.name' },
        { text: this.$t('inventory.type'), value: 'transactionType' },
        { text: this.$t('inventory.quantity'), value: 'quantity' },
        { text: this.$t('inventory.description'), value: 'description' },
        { text: this.$t('inventory.date'), value: 'createdAt' },
      ],
    };
  },
  methods: {
    async loadSiteBalances() {
      this.loadingBalances = true;
      try {
        this.siteBalances = await inventoryApi.getSiteBalances(this.site.id);
      } catch (error) {
        console.error('Error loading site balances:', error);
      } finally {
        this.loadingBalances = false;
      }
    },

    async loadDispatchNotes() {
      this.loadingDispatchNotes = true;
      try {
        this.dispatchNotes = await inventoryApi.getSiteDispatchNotes(this.site.id);
      } catch (error) {
        console.error('Error loading dispatch notes:', error);
      } finally {
        this.loadingDispatchNotes = false;
      }
    },

    async loadTransactions() {
      this.loadingTransactions = true;
      try {
        this.transactions = await inventoryApi.getSiteTransactionHistory(this.site.id);
      } catch (error) {
        console.error('Error loading transactions:', error);
      } finally {
        this.loadingTransactions = false;
      }
    },

    openDispatchNoteDialog() {
      this.$refs.dispatchNoteDialog.open();
    },

    editDispatchNote(dispatchNote) {
      this.$refs.dispatchNoteDialog.openEdit(dispatchNote);
    },

    editCompletedDispatchNote(dispatchNote) {
      this.$refs.dispatchNoteDialog.openEditCompleted(dispatchNote);
    },

    async onDispatchSaved({ completedEdit }) {
      if (completedEdit) {
        await Promise.all([
          this.loadDispatchNotes(),
          this.loadSiteBalances(),
          this.loadTransactions(),
        ]);
      } else {
        await this.loadDispatchNotes();
      }
    },

    async completeDispatchNote(dispatchNote) {
      const confirmed = await this.$refs.confirmDialog.open(
        this.$t('inventory.confirm_complete.title'),
        this.$t('inventory.confirm_complete.message'),
        { color: 'success' }
      );

      if (confirmed) {
        try {
          await inventoryApi.completeDispatchNote(dispatchNote.id);
          this.showSuccess(this.$t('inventory.success.dispatch_completed'));
          await Promise.all([
            this.loadDispatchNotes(),
            this.loadSiteBalances(),
            this.loadTransactions(),
          ]);
        } catch (error) {
          console.error('Error completing dispatch note:', error);
          this.showError(this.$t('inventory.errors.dispatch_complete_failed'));
        }
      }
    },

    async deleteDispatchNote(dispatchNote) {
      const confirmed = await this.$refs.confirmDialog.open(
        this.$t('inventory.confirm_delete.title'),
        this.$t('inventory.confirm_delete.message'),
        { color: 'error' }
      );

      if (confirmed) {
        try {
          await inventoryApi.deleteDispatchNote(dispatchNote.id);
          this.showSuccess(this.$t('inventory.success.dispatch_deleted'));
          await this.loadDispatchNotes();
        } catch (error) {
          console.error('Error deleting dispatch note:', error);
          this.showError(this.$t('inventory.errors.dispatch_delete_failed'));
        }
      }
    },

    viewDispatchNote(dispatchNote) {
      this.selectedDispatchNote = dispatchNote;
      this.viewDispatchDialog = true;
    },

    formatQuantity(value) {
      if (value === null || value === undefined) return '0';
      return Number(value).toFixed(2).replace(/\.?0+$/, '');
    },

    formatDate(dateTime) {
      if (!dateTime) return '';
      return new Date(dateTime).toLocaleDateString('fi-FI');
    },

    formatDateTime(dateTime) {
      if (!dateTime) return '';
      return new Date(dateTime).toLocaleString('fi-FI');
    },

    getTransactionTypeColor(type) {
      const colors = {
        IN: 'success',
        OUT: 'warning',
        ADJUSTMENT: 'info',
        TRANSFER: 'primary',
      };
      return colors[type] || 'grey';
    },

    showSuccess(message) {
      this.$store.dispatch('showNotification', {
        color: 'success',
        message: message,
        timeOut: 2000,
      });
    },

    showError(message) {
      this.$store.dispatch('showNotification', {
        color: 'error',
        message: message,
        timeOut: 3000,
      });
    },
  },
  async mounted() {
    // Expand transaction history by default on desktop, collapse on mobile
    this.transactionHistoryExpanded = this.$vuetify.breakpoint.xsOnly ? undefined : 0;

    await Promise.all([
      this.loadSiteBalances(),
      this.loadDispatchNotes(),
      this.loadTransactions(),
    ]);
  },
};
</script>
